import * as React from 'react';

import { useDisplayTimeZone } from '@/hooks/useDisplayTimeZone';
import {
  coerceToUtcDate,
  formatUtcDate,
  formatDateTime,
  formatDateTimeToMinute,
  type DateInput,
} from '@/lib/date-utils';
import { cn } from '@/lib/utils';

/**
 * Display variant.
 *
 * - `'datetime'` — date + time in the user's display time zone and absolute
 *   format (Settings → Timestamp Format), ending in the zone's short name
 *   (`" UTC"` by default).
 *   Default for any backend-recorded instant where the clock matters.
 * - `'minute'` — `'datetime'` to the minute, no seconds. For values picked to
 *   the minute, like date filter boundaries.
 * - `'date'` — UTC calendar date only (in the user's date format), no clock;
 *   ends in `" UTC"` when the display zone is not UTC.
 *   Use for expirations and "as of" cells where the time-of-day is noise.
 */
export type DateTimeFormat = 'datetime' | 'minute' | 'date';

export type DateTimeProps = Omit<
  React.ComponentProps<'time'>,
  'dateTime' | 'children'
> & {
  /**
   * The instant to render. Accepts ISO strings, `Date` objects, epoch
   * millis, or nullish — unparseable input renders the `fallback`.
   */
  value: DateInput;
  /** Default `'datetime'`. See {@link DateTimeFormat}. */
  format?: DateTimeFormat;
  /** Rendered when `value` is nullish or unparseable. Default `'—'`. */
  fallback?: string;
};

/**
 * Render a backend-recorded timestamp consistently across the FE.
 *
 * The `'datetime'` variant renders in the user's display time zone and always
 * names it, so no viewer misreads the clock; `'date'` stays pinned to UTC and
 * says so outside UTC — see the comment block in `lib/date-utils.ts` for the rationale.
 *
 * Emits a `<time dateTime="…">` element when the input is parseable so
 * assistive tech and crawlers can consume the machine-readable instant.
 *
 * @example
 *   <DateTime value={user.createdAt} />                 // date, time, zone
 *   <DateTime value={mute.mutedUntil} format="date" />  // calendar day only
 *   <DateTime value={undefined} fallback="never" />     // 'never'
 */
const DateTime = React.forwardRef<HTMLTimeElement, DateTimeProps>(
  ({ value, format = 'datetime', fallback = '—', className, ...rest }, ref) => {
    const timeZone = useDisplayTimeZone();
    // Parsed once, so the text and `dateTime` read a zone-less string alike.
    // Only a parsed input gets `dateTime=…`: a fallback cell isn't machine-readable.
    const date = coerceToUtcDate(value);
    const formatted =
      format === 'date'
        ? formatUtcDate(date, timeZone !== 'UTC', fallback)
        : format === 'minute'
          ? formatDateTimeToMinute(date, timeZone, fallback)
          : formatDateTime(date, timeZone, fallback);

    return (
      <time
        ref={ref}
        dateTime={date?.toISOString()}
        className={cn('whitespace-nowrap', className)}
        {...rest}
      >
        {formatted}
      </time>
    );
  }
);
DateTime.displayName = 'DateTime';

export { DateTime };
